import React, { useState, useCallback } from "react";
import { Card, Button, Alert, ListGroup, ProgressBar } from "react-bootstrap";
import * as pdfjsLib from "pdfjs-dist";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Ensure PDF.js worker is configured
if (typeof window !== "undefined" && "Worker" in window) {
  pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
}

interface PDFFileInfo {
  file: File;
  totalPages: number;
  status: "pending" | "processing" | "complete" | "error";
  error?: string;
}

export const PDFPageCounter: React.FC = () => {
  const [files, setFiles] = useState<PDFFileInfo[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const totalPages = files.reduce(
    (sum, file) => (file.status === "complete" ? sum + file.totalPages : sum),
    0
  );

  const processFile = async (file: File): Promise<number> => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
      const pdf = await loadingTask.promise;
      return pdf.numPages;
    } catch (error) {
      console.error("Error processing PDF:", error);
      throw new Error("Failed to read PDF file");
    }
  };

  const handleFiles = useCallback(
    async (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) return;

      const pdfFiles: PDFFileInfo[] = Array.from(fileList)
        .filter((file) => file.type === "application/pdf")
        .map((file) => ({
          file,
          totalPages: 0,
          status: "pending" as const,
        }));

      if (pdfFiles.length === 0) {
        alert("Please select PDF files only");
        return;
      }

      // Capture the starting index before adding files
      const startIndex = files.length;

      setFiles((prev) => [...prev, ...pdfFiles]);
      setIsProcessing(true);

      // Process files sequentially to avoid overwhelming the browser
      for (let i = 0; i < pdfFiles.length; i++) {
        const fileIndex = startIndex + i;
        try {
          setFiles((prev) =>
            prev.map((f, idx) =>
              idx === fileIndex ? { ...f, status: "processing" as const } : f
            )
          );

          const pages = await processFile(pdfFiles[i].file);

          setFiles((prev) =>
            prev.map((f, idx) =>
              idx === fileIndex
                ? { ...f, totalPages: pages, status: "complete" as const }
                : f
            )
          );
        } catch (error) {
          setFiles((prev) =>
            prev.map((f, idx) =>
              idx === fileIndex
                ? {
                    ...f,
                    status: "error" as const,
                    error: (error as Error).message,
                  }
                : f
            )
          );
        }
      }

      setIsProcessing(false);
    },
    [files.length]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  const handleDrag = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
  };

  const clearFiles = () => {
    setFiles([]);
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Card className="shadow-sm">
      <Card.Body>
        <h5 className="mb-3">
          <FontAwesomeIcon icon="calculator" className="mr-2" />
          Not Sure How Many Pages You Need?
        </h5>
        <p className="text-muted">
          Upload your PDFs here (processed locally, never sent to our servers)
          and we'll count the pages for you.
        </p>

        {/* Upload Area */}
        <div
          className={`border-2 border-dashed rounded p-4 text-center mb-3 ${
            dragActive ? "border-primary bg-light" : "border-secondary"
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          style={{ cursor: "pointer", transition: "all 0.2s" }}
        >
          <FontAwesomeIcon
            icon="cloud-upload-alt"
            size="3x"
            className="text-muted mb-3"
          />
          <p className="mb-2">
            <strong>Drag and drop PDF files here</strong>
          </p>
          <p className="text-muted mb-3">or</p>
          <label htmlFor="pdf-file-input">
            <Button variant="outline-primary" as="span" disabled={isProcessing}>
              <FontAwesomeIcon icon="folder-open" className="mr-2" />
              Browse Files
            </Button>
          </label>
          <input
            id="pdf-file-input"
            type="file"
            accept=".pdf,application/pdf"
            multiple
            onChange={handleFileInput}
            style={{ display: "none" }}
            disabled={isProcessing}
          />
        </div>

        {/* File List */}
        {files.length > 0 && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">Uploaded Files ({files.length})</h6>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={clearFiles}
                disabled={isProcessing}
              >
                Clear All
              </Button>
            </div>
            <ListGroup
              className="mb-3"
              style={{ maxHeight: "300px", overflowY: "auto" }}
            >
              {files.map((fileInfo, index) => (
                <ListGroup.Item key={index}>
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon
                          icon="file-pdf"
                          className="text-danger mr-2"
                        />
                        <span
                          className="text-truncate"
                          style={{ maxWidth: "300px" }}
                        >
                          {fileInfo.file.name}
                        </span>
                      </div>
                      {fileInfo.status === "processing" && (
                        <ProgressBar
                          animated
                          now={100}
                          className="mt-2"
                          style={{ height: "4px" }}
                        />
                      )}
                      {fileInfo.status === "error" && (
                        <small className="text-danger d-block mt-1">
                          {fileInfo.error}
                        </small>
                      )}
                    </div>
                    <div className="d-flex align-items-center">
                      {fileInfo.status === "complete" && (
                        <span className="badge badge-success mr-2">
                          {fileInfo.totalPages} pages
                        </span>
                      )}
                      {fileInfo.status === "processing" && (
                        <span className="badge badge-info mr-2">
                          Processing...
                        </span>
                      )}
                      {fileInfo.status === "error" && (
                        <span className="badge badge-danger mr-2">Error</span>
                      )}
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removeFile(index)}
                        disabled={isProcessing}
                      >
                        <FontAwesomeIcon icon="times" />
                      </Button>
                    </div>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </>
        )}

        {/* Total Count */}
        {totalPages > 0 && (
          <Alert variant="primary">
            <div>
              <h5 className="mb-1">
                <FontAwesomeIcon icon="check-circle" className="mr-2" />
                Total: {totalPages.toLocaleString()} pages
              </h5>
              <p className="mb-0 small">
                💡 <strong>Pro tip:</strong> You can exclude blank pages or
                pages without relevant data during extraction, so they won't
                count towards your page limit.
              </p>
            </div>
          </Alert>
        )}
      </Card.Body>
    </Card>
  );
};
